import { Divider, Space, Text } from "@/atoms"
import { InformationPopover } from "@/molecules/InformationPopover"
import { RemediationHeader } from "@/molecules/RemediationHeader"
import { SkeletonsBlock } from "@/molecules/SkeletonsBlock"
import { BaseImageFixes } from "./components/Fixes/BaseImageFixes/BaseImageFixes"
import { DockerfileImageFixes } from "./components/Fixes/DockerfileImageFixes"
import { PackageUpdates } from "./components/Fixes/PackageUpdates"
import { ThirdPartyFixes } from "./components/Fixes/ThirdPartyFixes"

import type {
  ApplicativeImageFinding,
  BaseImageFinding,
  CodeRepoFinding,
  ThirdPartyFinding,
  VmFinding
} from "@/domain/issue"
import type { RemediationGuideProps } from "./RemediationGuide.types"

import { hasDockerfileFixes } from "./components/Fixes/DockerfileImageFixes"
import styles from "./RemediationCard.module.scss"

export const RemediationGuide = ({
  t,
  tKey,
  remediationEffort,
  hasFixVersion,
  response,
  isLoading,
  copyable
}: RemediationGuideProps) => {
  if (isLoading || !response) {
    return <SkeletonsBlock />
  }

  const {
    metadata: { packageName, issueType },
    fixVersions,
    codeRepoFindings = [],
    baseImageFindings = [],
    thirdPartyFindings = [],
    imageFindings = [],
    vmFindings = []
  } = response
  const showFixVersion =
    hasFixVersion &&
    fixVersions &&
    fixVersions.length > 0 &&
    issueType !== "Malicious_Package"

  const imagesFindingsWithFixes = imageFindings?.filter(
    (f: ApplicativeImageFinding) => f.remedies?.length > 0
  )
  const hasImageFindingsWithFixes = imagesFindingsWithFixes.length > 0
  const hasDockerfileImageFixes = hasDockerfileFixes(imagesFindingsWithFixes)
  const hasNonDockerfileImageFixes = imagesFindingsWithFixes.some((f) =>
    f.remedies?.some((fix) => fix.source !== "dockerfile")
  )
  const hasBaseImageFindingsWithFixes = baseImageFindings.some(
    (f: BaseImageFinding) => f.remedies?.length > 0
  )
  const hasThirdPartyFindingsWithFixes = thirdPartyFindings.some(
    (f: ThirdPartyFinding) => f.remedies?.length > 0
  )
  const hasVmFindingsWithFixes = vmFindings.some(
    (f: VmFinding) => f.remedies?.length > 0
  )
  const {
    hasCodeRepoFindingsWithFixes,
    hasCodeRepoFindingsWithErrors,
    hasCodeRepoFindingsWithHierarchy
  } = codeRepoFindings.reduce(
    (
      {
        hasCodeRepoFindingsWithFixes,
        hasCodeRepoFindingsWithErrors,
        hasCodeRepoFindingsWithHierarchy
      },
      f: CodeRepoFinding
    ) => ({
      hasCodeRepoFindingsWithFixes:
        hasCodeRepoFindingsWithFixes || f.remedies.length > 0,
      hasCodeRepoFindingsWithErrors:
        hasCodeRepoFindingsWithErrors || !!f.computationError?.length,
      hasCodeRepoFindingsWithHierarchy:
        hasCodeRepoFindingsWithHierarchy || f.introducedThrough.length > 0
    }),
    {
      hasCodeRepoFindingsWithFixes: false,
      hasCodeRepoFindingsWithErrors: false,
      hasCodeRepoFindingsWithHierarchy: false
    }
  )

  const showSourcesUpdates =
    hasCodeRepoFindingsWithFixes ||
    hasCodeRepoFindingsWithErrors ||
    (hasCodeRepoFindingsWithHierarchy && showFixVersion)
  const showPackagesUpdates = showSourcesUpdates || hasNonDockerfileImageFixes
  const showDockerfileImageFixes = hasDockerfileImageFixes
  const showBaseImage =
    hasBaseImageFindingsWithFixes ||
    (baseImageFindings.length > 0 && showFixVersion)
  const showThirdParty =
    hasThirdPartyFindingsWithFixes ||
    (thirdPartyFindings.length > 0 && showFixVersion)
  const showRemediationGuide =
    showPackagesUpdates ||
    showDockerfileImageFixes ||
    showBaseImage ||
    showThirdParty
  const showRemediationGuideHeader =
    hasCodeRepoFindingsWithFixes ||
    hasImageFindingsWithFixes ||
    hasBaseImageFindingsWithFixes ||
    hasThirdPartyFindingsWithFixes ||
    hasVmFindingsWithFixes
  const showNoFix = !showRemediationGuide && !showFixVersion
  const remediationComponents: JSX.Element[] = []

  if (showPackagesUpdates) {
    remediationComponents.push(
      <PackageUpdates
        copyable={copyable}
        showSources={showSourcesUpdates}
        hasCodeRepoFixes={hasCodeRepoFindingsWithFixes}
        codeRepoFindings={codeRepoFindings}
        imageFindings={imagesFindingsWithFixes}
        t={t}
        tKey={tKey}
      />
    )
  }
  if (showDockerfileImageFixes) {
    remediationComponents.push(
      <DockerfileImageFixes
        copyable={copyable}
        imageFindings={imagesFindingsWithFixes}
        t={t}
        tKey={tKey}
      />
    )
  }
  if (showBaseImage) {
    const baseImageFixes = (
      <BaseImageFixes
        copyable={copyable}
        baseImageFindings={baseImageFindings}
        t={t}
        tKey={tKey}
      />
    )

    if (hasCodeRepoFindingsWithFixes || !hasBaseImageFindingsWithFixes) {
      remediationComponents.push(baseImageFixes)
    } else {
      remediationComponents.splice(0, 0, baseImageFixes)
    }
  }
  if (showThirdParty) {
    const thirdPartyFixes = (
      <ThirdPartyFixes
        copyable={copyable}
        thirdPartyFindings={thirdPartyFindings}
        t={t}
        tKey={tKey}
      />
    )

    if (hasCodeRepoFindingsWithFixes || !hasThirdPartyFindingsWithFixes) {
      remediationComponents.push(thirdPartyFixes)
    } else {
      remediationComponents.splice(0, 0, thirdPartyFixes)
    }
  }

  return (
    <Space
      orientation="vertical"
      separator={<Divider variant="dashed" className={styles.spaceDivider} />}
    >
      {showRemediationGuide && (
        <Space orientation="vertical" size={16}>
          {showRemediationGuideHeader && (
            <RemediationHeader
              t={t}
              tKey={tKey}
              remediationEffort={remediationEffort}
            />
          )}
          {remediationComponents}
        </Space>
      )}
      {showFixVersion && (
        <Space orientation="vertical">
          <Space size={4} inline>
            <Text className={styles.subtitle}>
              {`${t(`${tKey}fixesVersions`)}:`}
            </Text>
            <InformationPopover.Popover
              content={
                <InformationPopover.CommonTemplate>
                  {t(`${tKey}fixesInPackageVersionsPopover`)}
                </InformationPopover.CommonTemplate>
              }
            />
          </Space>
          <div>
            <Text className={styles.value}>
              {`${packageName} ${fixVersions.join(", ")}`}
            </Text>
          </div>
        </Space>
      )}
      {showNoFix && <Text>{t(`${tKey}noKnowFix`)}</Text>}
    </Space>
  )
}
